import React from 'react';
import { ChevronRight, Share2, UserPlus, Trophy, Calendar, Users } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import { btn } from '../../utils/buttonStyles';

export default function TournamentCard({
  tournament,
  isMyTournament = false,
  onView,
  onShare,
  onJoin
}) {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const renderIcon = () => {
    if (tournament.tournamentImage) {
      return (
        <img
          src={resolveImageUrl(tournament.tournamentImage)}
          alt={tournament.name}
          className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
        />
      );
    }
    if (tournament.emoji) {
      return (
        <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-base flex-shrink-0">
          {tournament.emoji}
        </span>
      );
    }
    return (
      <span className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-600 flex-shrink-0">
        {(tournament.name || '?')[0].toUpperCase()}
      </span>
    );
  };

  const getStatusStyle = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('ongoing') || s.includes('in progress')) return 'bg-green-100 text-green-800';
    if (s.includes('registration')) return 'bg-blue-100 text-blue-800';
    if (s.includes('completed')) return 'bg-gray-100 text-gray-800';
    if (s.includes('cancelled')) return 'bg-red-100 text-red-800';
    if (s.includes('draft')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-orange-100 text-orange-800';
  };

  return (
    <div className={`${theme.card} rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border ${theme.border} h-full flex flex-col`}>
      <div className="flex items-start justify-between mb-4 flex-1">
        <div className="flex-1 flex gap-3">
          {renderIcon()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className={`font-bold text-lg ${theme.text} truncate`}>{tournament.name}</h3>
            </div>
            {tournament.status && (
              <span className={`px-2 py-0.5 rounded-full text-xs inline-block mb-2 ${getStatusStyle(tournament.status)}`}>
                {tournament.status}
              </span>
            )}

            <p className={`${theme.textSecondary} text-sm mb-3 line-clamp-2 min-h-[2.5rem]`}>
              {tournament.description || 'No description available'}
            </p>

            <div className={`flex items-center flex-wrap gap-2 text-xs ${theme.textMuted} mb-4`}>
              {tournament.startDate && (
                <div className="flex items-center gap-x-1">
                  <Calendar className="w-3 h-3" />
                  <span>{tournament.startDate}</span>
                </div>
              )}
              {tournament.sport && tournament.sport !== 'General' && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                  {tournament.sport}
                </span>
              )}
            </div>

            {/* No organizer means the record is broken (organizer_user_id is
                NULL, so nobody can pass assertIsOrganizer), not that the
                organizer is merely unknown — so drop the clause entirely
                rather than printing a placeholder that reads like a real
                person. Location still shows on its own. */}
            <p className={`text-xs ${theme.textMuted}`}>
              {tournament.organizerName
                ? `Organized by ${tournament.organizerName}${tournament.location ? ` • ${tournament.location}` : ''}`
                : tournament.location}
            </p>
            {tournament.ownerType === 'group' && tournament.ownerName && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/squads/${tournament.ownerId}`);
                }}
                className={`mt-1 flex items-center gap-x-1 text-xs ${theme.link} ${theme.linkHover} transition-colors`}
              >
                <Users className="w-3 h-3" />
                <span>{tournament.ownerName}</span>
              </button>
            )}
            {tournament.ownerType === 'academy' && tournament.ownerName && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/academy/${tournament.ownerId}`);
                }}
                className={`mt-1 flex items-center gap-x-1 text-xs ${theme.link} ${theme.linkHover} transition-colors`}
              >
                <Users className="w-3 h-3" />
                <span>{tournament.ownerName}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-700/30">
        <div className="flex items-center gap-x-2">
          {isMyTournament && (
            <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
              <Trophy className="w-3 h-3 inline mr-1" />Organizer
            </span>
          )}
        </div>

        <div className={`${btn.group} flex-wrap justify-end`}>
          {onShare && (
            <button
              onClick={(e) => { e.stopPropagation(); onShare(tournament); }}
              className={`${btn.base} ${btn.md} ${theme.buttonGhost} flex items-center gap-x-1`}
            >
              <Share2 className="w-3 h-3" />
              <span>Share</span>
            </button>
          )}
          {onView && (
            <button
              onClick={(e) => { e.stopPropagation(); onView(tournament); }}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1`}
            >
              <span>View</span>
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
          {!isMyTournament && onJoin && tournament.registrationDeadlineRaw && new Date(tournament.registrationDeadlineRaw) > new Date() && (
            <button
              onClick={(e) => { e.stopPropagation(); onJoin(tournament.id); }}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-x-1`}
            >
              <UserPlus className="w-3 h-3" />
              <span>Register</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
